/**
 * theme-catalog (2026-08-20) — **테마 설정** 화면이 보여 주는 완성 테마 12종.
 *
 * 왜 카드인가: 색 동그라미만 찍어 두면 고른 결과가 어떤 화면인지 알 수 없다(2026-08-20 시안).
 * 라이트·다크와 색을 미리 조합해 두고, 카드를 고르면 **모드와 색을 한꺼번에** 바꾼다.
 *
 * 저장 자리는 새로 만들지 않는다 — 이미 있는 계정 칸(migration 106: themeMode ·
 * themePrimary/Accent/Danger)에 그대로 쓴다. 그래서 서버 변경이 없다.
 *
 * ★ 카드는 **주색·보조색·위험색만** 정한다. 배경·표면·글자 같은 중성색은 tokens.css 가
 *   모드별로 이미 갖고 있고, 카드가 그걸 덮으면 라이트에서 만든 값이 다크에서 글자를 지운다.
 *   카드의 라이트/다크는 "이 색 조합이 어느 모드에서 어울리는가" 를 뜻한다.
 *
 * 같은 목록을 "테마 만들기 > 불러오기" 도 쓴다([[theme-presets]]) — 이름이 두 화면에서
 * 갈리지 않게 여기 하나만 고친다.
 */

import type { SettingsI18nKey } from './settings-i18n';

export type CatalogMode = 'light' | 'dark';

export interface CatalogTheme {
  id: string;
  nameKey: SettingsI18nKey;
  /** 이 색 조합이 어울리는 모드 — 카드를 고르면 이 모드로 함께 넘어간다. */
  mode: CatalogMode;
  primary: string;
  accent: string;
  danger: string;
}

/** 미리보기 타일이 쓰는 모드별 중성색 — tokens.css 의 light/dark 공용 팔레트와 같은 값. */
export const NEUTRALS: Record<CatalogMode, { bg: string; surface: string; border: string; text: string }> = {
  light: { bg: '#ffffff', surface: '#f1f5f9', border: '#e2e8f0', text: '#1e293b' },
  dark: { bg: '#0f172a', surface: '#1e293b', border: '#475569', text: '#f1f5f9' },
};

/** 라이트 7 · 다크 5 = 12종. 순서가 곧 화면 순서다. */
export const THEME_CATALOG: ReadonlyArray<CatalogTheme> = [
  // ── 라이트 7 ──
  { id: 'ocean', nameKey: 'settings.theme.p_ocean', mode: 'light', primary: '#3b82f6', accent: '#6366f1', danger: '#ef4444' },
  { id: 'indigo', nameKey: 'settings.theme.p_indigo', mode: 'light', primary: '#6366f1', accent: '#a855f7', danger: '#ef4444' },
  { id: 'forest', nameKey: 'settings.theme.p_forest', mode: 'light', primary: '#22c55e', accent: '#14b8a6', danger: '#ef4444' },
  { id: 'sunset', nameKey: 'settings.theme.p_sunset', mode: 'light', primary: '#f97316', accent: '#f59e0b', danger: '#dc2626' },
  { id: 'rose', nameKey: 'settings.theme.p_rose', mode: 'light', primary: '#ec4899', accent: '#a855f7', danger: '#ef4444' },
  { id: 'purple', nameKey: 'settings.theme.p_purple', mode: 'light', primary: '#a855f7', accent: '#6366f1', danger: '#ef4444' },
  { id: 'teal', nameKey: 'settings.theme.p_teal', mode: 'light', primary: '#14b8a6', accent: '#0ea5e9', danger: '#f43f5e' },
  // ── 다크 5 ── (같은 색을 다크에 그대로 쓰면 탁해진다 — 한 단계 밝은 값으로 바꾼 조합이다)
  { id: 'midnight', nameKey: 'settings.theme.p_midnight', mode: 'dark', primary: '#60a5fa', accent: '#818cf8', danger: '#f87171' },
  { id: 'deep-purple', nameKey: 'settings.theme.p_deep_purple', mode: 'dark', primary: '#c084fc', accent: '#a78bfa', danger: '#f87171' },
  { id: 'dark-forest', nameKey: 'settings.theme.p_dark_forest', mode: 'dark', primary: '#4ade80', accent: '#2dd4bf', danger: '#f87171' },
  { id: 'amber-night', nameKey: 'settings.theme.p_amber_night', mode: 'dark', primary: '#fbbf24', accent: '#fb923c', danger: '#f87171' },
  { id: 'carbon', nameKey: 'settings.theme.p_carbon', mode: 'dark', primary: '#94a3b8', accent: '#38bdf8', danger: '#f87171' },
];

/** 두 색을 비율로 섞는다 — 영역별 색을 주색 하나에서 만들 때 쓴다. */
/**
 * 「기본」 — 색을 **얹지 않는** 상태. 앱 자신의 토큰이 그대로 쓰인다
 * (라이트: 레일·카드 `--paper` 흰 종이 · 바닥 `--field` 옅은 회색 — 디자인 가이드 F2).
 *
 * ★ `THEME_CATALOG` 에 넣지 않는다 — `matchCatalog` 는 **주색으로 짝을 찾는데** 기본은 주색이 없다.
 *   목록에 끼우면 「주색 없음」이 12종 중 하나로 잘못 잡힌다.
 * ★ 이것이 없어서 오션 블루가 «기본값처럼» 보였다(2026-09-02 사용자 지적).
 */
export const DEFAULT_THEME_ID = 'default';

/** 기본 카드의 미리보기 색 — 주색을 쓰지 않고 앱의 중립값만 보여 준다. */
export const DEFAULT_THEME_SWATCH = ['#ffffff', '#f1f5f9', '#e2e8f0'] as const;

export function mixHex(hex: string, ratio: number, base: string): string {
  const a = hex.replace('#', '');
  const b = base.replace('#', '');
  const out = [0, 2, 4].map((i) => {
    const x = parseInt(a.slice(i, i + 2), 16);
    const y = parseInt(b.slice(i, i + 2), 16);
    return Math.round(x * ratio + y * (1 - ratio));
  });
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export interface AreaPalette {
  bg: string; rail: string; menu: string; rrail: string; status: string;
  table: string; tableHead: string; tableHeadText: string;
  icon: string; iconHover: string; tint: string; line: string; text: string; textMuted: string;
}

/**
 * 테마 카드 → **영역별 색**(2026-08-20 사용자 확정).
 *
 * 레일·상태바·표 헤더를 회색으로 고정해 두면 테마를 바꿔도 화면 절반이 그대로라
 * 갈아입은 티가 나지 않는다. 그래서 주색에서 농도만 달리해 각 영역을 만든다 —
 * 레일 10% · 상태바 14% · 표 헤더 12% 처럼 **뒤로 갈수록 진하게** 두어 위계가 남는다.
 */
export function paletteFor(card: CatalogTheme): AreaPalette {
  const p = card.primary;
  if (card.mode === 'dark') {
    return {
      bg: mixHex(p, 0.06, '#0f172a'),
      rail: mixHex(p, 0.14, '#111c31'),
      menu: mixHex(p, 0.10, '#162034'),
      rrail: mixHex(p, 0.14, '#111c31'),
      status: mixHex(p, 0.18, '#0d1626'),
      table: mixHex(p, 0.04, '#0f172a'),
      tableHead: mixHex(p, 0.16, '#1e293b'),
      tableHeadText: mixHex(p, 0.35, '#e2e8f0'),
      icon: mixHex(p, 0.30, '#94a3b8'),
      iconHover: p,
      tint: mixHex(p, 0.28, '#0f172a'),
      line: mixHex(p, 0.16, '#475569'),
      text: '#f1f5f9',
      textMuted: mixHex(p, 0.18, '#94a3b8'),
    };
  }
  return {
    bg: '#ffffff',
    rail: mixHex(p, 0.10, '#ffffff'),
    menu: mixHex(p, 0.06, '#ffffff'),
    rrail: mixHex(p, 0.10, '#ffffff'),
    status: mixHex(p, 0.14, '#ffffff'),
    table: '#ffffff',
    tableHead: mixHex(p, 0.12, '#ffffff'),
    tableHeadText: mixHex(p, 0.55, '#334155'),
    icon: mixHex(p, 0.40, '#64748b'),
    iconHover: p,
    tint: mixHex(p, 0.16, '#ffffff'),
    line: mixHex(p, 0.14, '#e2e8f0'),
    text: mixHex(p, 0.10, '#1e293b'),
    textMuted: mixHex(p, 0.25, '#64748b'),
  };
}

/** 지금 설정이 어느 카드인지 — 색 3개가 모두 같을 때만 그 카드로 본다(모드는 따로 표시). */
export function matchCatalog(
  colors: { primary?: string | null; accent?: string | null; danger?: string | null },
): CatalogTheme | null {
  return THEME_CATALOG.find((t) => t.primary === colors.primary
    && t.accent === colors.accent
    && t.danger === colors.danger) ?? null;
}
